import React, { useRef } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { Filter } from '../../../models/filter/filter';
import { Api } from '../../../services/api';
import { Notify } from '../../../services/notification/notify';
import { SearchInput } from '../inputs/search';
import { useHasPermission } from '../../../services/auth/auth';
import { ActionButtonModes } from '../actions/actionButtons';
import { PageController } from '../grid/queryGrid/pageController';
import { useSearchParams } from 'react-router-dom';


import styles from './query.module.css';
import { formatNumber } from '../../../services/formatting';
import { SelectQuery } from '../inputs/selectQuery';
import { Switch } from '../inputs/switch';
import { SearchFilter } from '../filter/filter';

/**
 * @param {Object} props
 * @param {string} props.singleResourceName Single name (Example: branch)
 * @param {string} props.pluralResourceName plural name (Example: branches)
 * @param {string} props.restEndpoint Rest Endpoint of the API to query (Example: /locations/branches)
 * @param {string} props.listUrl List url for the webapp
 * @param {function(Object.<string, Object>): string} props.nameFunction The function for getting the name from the raw API data
 * @param {boolean} [props.enableSearchBar=true] Is the search bar shown
 * @param {boolean} [props.queryOnLoad=true] Should a query be performed when the component loads
 * @param {number?} [props.scope=null] Scope to be passed to the API
 * @param {number?} [props.maxResults=null] Max Results to be returned by the API
 * @param {((props: any) => JSX.Element)[]} [props.actions=[]] Actions
 * @param {string[]} [props.readPermissions=[]] Read Permissions
 * @param {string[]} [props.writePermissions=[]] Write Permissions
 * @param {string|null} [props.refreshToken=null] On change, refreshes the data
 * @param {string|null} [props.queryParamPrefix=null] If given, will save data to query parameters. Prefix to all multiple tables a single endpoint.
 * @param {((v: { [x: string]: any; }[]) => void)|null} [props.onSelect=null] Called on Select
 * @param {{key: string, label: string} & ({type: 'text'} | {type: 'switch', options: string} | {type: 'select', restEndpoint: string, nameFunction: (v: any) => string, allOption: boolean})[]|null} [props.filters=null] Exposes filters to be passed
 * @param {any} [props.children]
 */
export const Query = ({
    singleResourceName,
    pluralResourceName,
    restEndpoint,
    listUrl,
    nameFunction = raw => raw.name,
    enableSearchBar = true,
    queryOnLoad = true,
    scope = null,
    maxResults = 30,
    actions = [],
    readPermissions = [],
    writePermissions = [],
    onSelect = null,
    refreshToken = null,
    queryParamPrefix = null,
    filters = null,
    children,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    /** @type [Object.<string, Object>[], function(Object.<string, Object>[]): void] */
    // @ts-ignore
    const [rawData, setRawData] = useState([]);

    const [page, setPage] = useState(
        queryParamPrefix != null
        ? Math.max(parseInt(searchParams.get(queryParamPrefix + 'p') ?? '1') - 1, 0)
        : 0
    );

    /** @type [{totalPages: number, totalCount: number, pageIndex: number}, function({totalPages: number, totalCount: number, pageIndex: number}): void] */
    // @ts-ignore
    const [pageData, setPageData] = useState({});
    const [loading, setLoading] = useState(false);
    const [disablePaging, setDisablePaging] = useState(false);
    const [query, setQuery] = useState(queryParamPrefix != null ? searchParams.get(queryParamPrefix + 'q') || null : null);
    /** @type [{field: string|null, direction: 'up'|'down'}, function({field: string|null, direction: 'up'|'down'}): void] */
    // @ts-ignore
    const [sort, setSort] = useState(queryParamPrefix != null && searchParams.has(queryParamPrefix + 'sf') && searchParams.has(queryParamPrefix + 'sd') ? {field: searchParams.get(queryParamPrefix + 'sf'), direction: searchParams.get(queryParamPrefix + 'sd')} : null);

    const [searchFilters, setSearchFilters] = useState({});


    /** @type [number[], function(number[]): void] */
    // @ts-ignore
    const [selected, setSelected] = useState([]);
    const [isEditing, setIsEditing] = useState(false);

    const canRead = useHasPermission(readPermissions);
    const canWrite = useHasPermission(writePermissions);

    useEffect(() => {
        onSelect?.(selected.map(id => rawData?.find(r => r.id === id) ?? {id: id}));
    }, [selected]);

    useEffect(() => {
        if (queryParamPrefix != null) {
            setSearchParams(sp => {
                if (query == null)
                    sp.delete(queryParamPrefix + 'q');
                else
                    sp.set(queryParamPrefix + 'q', query);
                return sp;
            }, { replace: true });
        }
    }, [query]);

    useEffect(() => {
        if (queryParamPrefix != null) {
            setSearchParams(sp => {
                if (page == 0)
                    sp.delete(queryParamPrefix + 'p');
                else
                    sp.set(queryParamPrefix + 'p', (page + 1).toString());
                return sp;
            }, { replace: true });
        }
    }, [page]);

    useEffect(() => {
        if (queryParamPrefix != null) {
            setSearchParams(sp => {
                if (sort == null || sort.field == null) {
                    sp.delete(queryParamPrefix + 'sf');
                    sp.delete(queryParamPrefix + 'sd');
                } else {
                    sp.set(queryParamPrefix + 'sf', sort.field);
                    sp.set(queryParamPrefix + 'sd', sort.direction);
                }

                return sp;
            }, { replace: true });
        }
    }, [sort]);

    const getData = () => {
        const filter = new Filter({
            query: query,
            sort: sort,
            page: page,
            pageSize: maxResults,
            scope: scope,
            filters: searchFilters,
        });
        setLoading(true);

        Api.get(restEndpoint, {q: JSON.stringify(filter)})
        .then(json => {

            setRawData(json.content);

            setPageData({
                totalPages: json.totalPages,
                totalCount: json.totalCount,
                pageIndex: json.pageIndex
            });

            setLoading(false);

        })
        .catch(e => {
            console.error(e);
            Notify.error(`Error searching for ${pluralResourceName}`, `An error occurred while trying search for ${pluralResourceName}.`);
            setLoading(false);
        });
    };

    const isFirstRender = useRef(true);
    const hasRunRef = useRef(false); // prevents duplicate Strict Mode calls

    useEffect(() => {
        if (hasRunRef.current) {
            return;
        }
        hasRunRef.current = true;

        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (!queryOnLoad) return; // skip first run if false
        }

        console.log('Running get data');
        getData();
    }, [query, page, sort, searchFilters, scope, refreshToken]);
    
    const selectedName = () => selected.length === 0 ? singleResourceName : pluralResourceName;
    const selectedNames = () => selected.map(id => nameFunction(rawData?.find(r => r.id === id) ?? [])).join(', ');

    const refreshData = async () => {
        getData();
    }

    return <section className={styles.Query}>
        <header className={styles.Header}>
            {(enableSearchBar) && (
                <div className={styles.SearchWrapper}>
                    <SearchInput
                        placeholder={'Search ' + pluralResourceName}
                        disabled={loading}
                        onSearch={(q, _, explicit) => {
                            setQuery(q === '' ? null : q);
                            setPage(0);

                            if (explicit)
                                refreshData();
                        }}
                        defaultValue={query}
                    />
                </div>
            )}

            {
                actions.map((Action, i) => <Action
                    key={Action.toString() + i.toString()}
                    name={selectedNames()}
                    ids={selected}
                    data={rawData}
                    scope={scope}
                    nameFunction={nameFunction}
                    resourceName={selectedName()}
                    restEndpoint={restEndpoint}
                    setSelected={setSelected}
                    refreshData={refreshData}
                    mode={ActionButtonModes.Query}
                    canRead={canRead}
                    canWrite={canWrite}
                    listUrl={listUrl}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    setLoading={setLoading}
                    disablePaging={disablePaging}
                    setDisablePaging={setDisablePaging}
                />)
            }

            {(filters && filters.length > 0) && (
                <div className={styles.Filters}>
                        {(filters ?? []).map((f) => (
                            <SearchFilter
                                filter={f}
                                searchFilters={searchFilters}
                                setSearchFilters={setSearchFilters}
                                loading={loading}
                            />
                        ))}
                </div>
            )}

            <div className={styles.SearchMessage}>
                {
                    loading ?
                    'Searching...'
                    :
                    `Showing ${formatNumber(pageData.totalCount ?? 0)} result${pageData.totalCount !== 1 ? 's' : ''} ${query ? ` for "${query}"` : '' }`
                }
            </div>
        </header>

        {
            children({
                loading,
                rawData,
                page,
                setPage,
                pageData,
                setPageData,
                sort,
                setSort,
                query,
                setQuery,
                getData,
                canWrite,
                canRead,
                selected,
                setSelected,
                isEditing,
                setIsEditing,
                refreshData,
                setLoading,
                disablePaging,
                setDisablePaging,
            })
        }

        <PageController
            pageIndex={pageData.pageIndex}
            totalPages={pageData.totalPages}
            pageState={[page, setPage]}
            disabled={loading || disablePaging}
        />

    </section>
};